/**
 * Product image viewer.
 *
 * A port of the order-tracker viewer (components/product-detail/ProductImageLightbox.tsx)
 * to a Shopify theme. The structure is the point: a flex column of header /
 * stage / hint / thumbnail rail, with the image a flow element inside a
 * bordered card. PhotoSwipe, which the theme ships, positions the image
 * absolutely and owns its geometry, so that layout is not reachable with it —
 * hence a purpose-built element rather than more chrome bolted onto PhotoSwipe.
 *
 * The reference uses Embla for the swipe and framer-motion for the zoom spring.
 * Both are a translate on an element; doing it directly keeps the theme free of
 * two more libraries, and the constants below are the reference's own.
 */
(() => {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 4;
  const DOUBLE_TAP_ZOOM = 2.5;
  const WHEEL_SENSITIVITY = 0.0018;
  const DRAG_SLOP = 8;

  const wrapIndex = (index, total) => (total > 0 ? ((index % total) + total) % total : 0);

  const clampZoom = (level) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, level));

  /** -1 previous, 1 next, 0 snap back. The reference's own feel: a fifth of the
   *  viewport, capped so a wide screen does not demand an absurd swipe. */
  const pageFromDrag = (dx, viewportWidth) => {
    const threshold = Math.min(120, viewportWidth * 0.18);

    if (dx <= -threshold) return 1;
    if (dx >= threshold) return -1;
    return 0;
  };

  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-lightbox-viewport]');
      this.track = this.querySelector('[data-lightbox-track]');
      this.counter = this.querySelector('[data-lightbox-counter]');
      this.resetButton = this.querySelector('[data-lightbox-reset]');
      this.resetLabel = this.resetButton?.dataset.label || 'Reset zoom';
      this.allSlides = Array.from(this.querySelectorAll('[data-lightbox-slide]:not(.lightbox__slide--clone)'));
      this.allThumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));

      this.index = 0;
      this.trackIndex = 0;
      this.zoom = MIN_ZOOM;
      this.pan = { x: 0, y: 0 };
      this.drag = null;
      this._settleTimeout = null;

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('wheel', this._onWheel.bind(this), { passive: false });
      this.addEventListener('dblclick', this._onDoubleClick.bind(this));
      this.viewport.addEventListener('pointerdown', this._onPointerDown.bind(this));
      this.viewport.addEventListener('pointermove', this._onPointerMove.bind(this));
      this.viewport.addEventListener('pointerup', this._onPointerUp.bind(this));
      this.viewport.addEventListener('pointercancel', this._onPointerUp.bind(this));
      this.track.addEventListener('transitionend', this._onTrackTransitionEnd.bind(this));
      this._onKeyDown = this._onKeyDown.bind(this);
    }

    /* -- open / close -- */

    _setupClones() {
      this.track.querySelectorAll('.lightbox__slide--clone').forEach((el) => el.remove());

      if (!this.slides || this.slides.length <= 1) {
        return;
      }

      const firstSlide = this.slides[0];
      const lastSlide = this.slides[this.slides.length - 1];

      const cloneLast = lastSlide.cloneNode(true);
      cloneLast.classList.add('lightbox__slide--clone');
      cloneLast.removeAttribute('data-lightbox-slide');
      cloneLast.setAttribute('aria-hidden', 'true');
      const imgLast = cloneLast.querySelector('img');
      if (imgLast) {
        imgLast.loading = 'eager';
      }

      const cloneFirst = firstSlide.cloneNode(true);
      cloneFirst.classList.add('lightbox__slide--clone');
      cloneFirst.removeAttribute('data-lightbox-slide');
      cloneFirst.setAttribute('aria-hidden', 'true');
      const imgFirst = cloneFirst.querySelector('img');
      if (imgFirst) {
        imgFirst.loading = 'eager';
      }

      this.track.insertBefore(cloneLast, this.slides[0]);
      this.track.appendChild(cloneFirst);
    }

    open(mediaId) {
      // Variant filtering can hide media; a display:none slide drops out of the
      // flex track, so the visible ones stay contiguous and the index maths holds.
      const gallery = document.querySelector(`[data-lightbox="${this.id}"]`);

      this.allSlides.forEach((slide) => {
        const filtered = gallery
          ?.querySelector(`.product__media-item[data-media-id="${slide.dataset.mediaId}"]`)
          ?.classList.contains('is-filtered');

        slide.hidden = Boolean(filtered);
      });

      this.allThumbs.forEach((thumb) => {
        thumb.hidden = Boolean(
          gallery
            ?.querySelector(`.product__media-item[data-media-id="${thumb.dataset.mediaId}"]`)
            ?.classList.contains('is-filtered')
        );
      });

      this.slides = this.allSlides.filter((slide) => !slide.hidden);
      this.thumbs = this.allThumbs.filter((thumb) => !thumb.hidden);

      if (this.slides.length === 0) {
        return;
      }

      this._setupClones();

      const wanted = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      const targetRealIndex = wanted === -1 ? 0 : wanted;

      // The slide we are about to show must not still be lazy, or the panel
      // paints empty until the browser gets round to it.
      [targetRealIndex - 1, targetRealIndex, targetRealIndex + 1].forEach((i) => {
        const image = this.slides[i]?.querySelector('img');
        if (image) {
          image.loading = 'eager';
          image.decoding = 'sync';
          if (!image.complete && image.src) image.src = image.src;
        }
      });

      this.hidden = false;
      this.previousFocus = document.activeElement;
      this.scrollLock = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      /* Marks the page while the zoom/lightbox is open so floating UI (sticky buy
         bar, WhatsApp button) can hide - they were rendering on top of the zoomed
         image and covering the product. */
      document.body.classList.add('meddey-lightbox-open');
      document.addEventListener('keydown', this._onKeyDown);

      this._goTo(targetRealIndex, false);
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.querySelector('[data-lightbox-close]')?.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onKeyDown);
      document.body.style.overflow = this.scrollLock || '';
      document.body.classList.remove('meddey-lightbox-open');

      clearTimeout(this._settleTimeout);

      const finish = () => {
        this.hidden = true;
        this._setZoom(MIN_ZOOM);
        this._normalizeTrack();
      };

      this.addEventListener('transitionend', function once(event) {
        if (event.target === this && event.propertyName === 'opacity') {
          this.removeEventListener('transitionend', once);
          finish();
        }
      });

      // A hidden element fires no transition, so never wait on one forever.
      setTimeout(finish, 400);
      this.previousFocus?.focus();
    }

    /* -- paging & endless looping -- */

    _normalizeTrack() {
      const total = this.slides ? this.slides.length : 0;
      if (total <= 1) return;

      if (this.trackIndex === total + 1) {
        this._snapToTrack(1);
      } else if (this.trackIndex === 0) {
        this._snapToTrack(total);
      }
    }

    _snapToTrack(trackIdx) {
      clearTimeout(this._settleTimeout);
      this.track.style.transition = 'none';
      this.trackIndex = trackIdx;
      this.track.style.transform = `translate3d(${-trackIdx * 100}%, 0, 0)`;
      void this.track.offsetWidth;
      this.track.style.transition = '';
    }

    _onTrackTransitionEnd(event) {
      if (event.target === this.track && event.propertyName === 'transform') {
        this._handleSettle();
      }
    }

    _handleSettle() {
      clearTimeout(this._settleTimeout);
      const total = this.slides ? this.slides.length : 0;
      if (total <= 1) return;

      if (this.trackIndex === total + 1) {
        this._snapToTrack(1);
      } else if (this.trackIndex === 0) {
        this._snapToTrack(total);
      }
    }

    _next() {
      const total = this.slides ? this.slides.length : 0;
      if (total <= 1) return;
      this._normalizeTrack();
      this._goToTrack(this.trackIndex + 1, true);
    }

    _prev() {
      const total = this.slides ? this.slides.length : 0;
      if (total <= 1) return;
      this._normalizeTrack();
      this._goToTrack(this.trackIndex - 1, true);
    }

    _goTo(index, animate = true) {
      const total = this.slides ? this.slides.length : 0;
      if (total <= 1) {
        this._goToTrack(0, animate);
        return;
      }

      if (animate) {
        if (index === this.index + 1) {
          this._next();
          return;
        }
        if (index === this.index - 1) {
          this._prev();
          return;
        }
      }

      this._normalizeTrack();
      const validIndex = wrapIndex(index, total);
      this._goToTrack(validIndex + 1, animate);
    }

    _goToTrack(trackIdx, animate = true) {
      const total = this.slides ? this.slides.length : 0;
      if (total === 0) return;

      clearTimeout(this._settleTimeout);

      if (total === 1) {
        this.index = 0;
        this.trackIndex = 0;
        this.track.style.transition = animate ? '' : 'none';
        this.track.style.transform = 'translate3d(0, 0, 0)';
        if (!animate) {
          void this.track.offsetWidth;
          this.track.style.transition = '';
        }
        this._updateUI(animate);
        return;
      }

      this.trackIndex = trackIdx;

      // Determine the real 0-based index
      if (this.trackIndex === 0) {
        this.index = total - 1;
      } else if (this.trackIndex === total + 1) {
        this.index = 0;
      } else {
        this.index = wrapIndex(this.trackIndex - 1, total);
      }

      this._setZoom(MIN_ZOOM);

      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translate3d(${-this.trackIndex * 100}%, 0, 0)`;

      if (!animate) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }

      this._updateUI(animate);

      if (animate && (this.trackIndex === 0 || this.trackIndex === total + 1)) {
        this._settleTimeout = setTimeout(() => {
          this._handleSettle();
        }, 380);
      }
    }

    _updateUI(animate = true) {
      const total = this.slides ? this.slides.length : 0;
      if (total === 0) return;

      if (this.counter) {
        this.counter.textContent = `${this.index + 1} / ${total}`;
      }

      this.thumbs.forEach((thumb, position) => {
        if (position === this.index) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: animate ? 'smooth' : 'auto' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.slides.forEach((slide, position) => {
        const image = slide.querySelector('img');

        slide.classList.toggle('is-selected', position === this.index);

        if (image && Math.abs(position - this.index) <= 1) {
          image.loading = 'eager';
        }
      });
    }

    /* -- zoom / pan -- */

    get _activeImage() {
      return this.slides?.[this.index]?.querySelector('img') || null;
    }

    _setZoom(level, keepPan = false) {
      this.zoom = clampZoom(level);

      if (!keepPan || this.zoom === MIN_ZOOM) {
        this.pan = { x: 0, y: 0 };
      }

      const zoomed = this.zoom > MIN_ZOOM;

      this.classList.toggle('is-zoomed', zoomed);
      this.viewport.style.touchAction = zoomed ? 'none' : 'pan-y pinch-zoom';

      if (this.resetButton) {
        this.resetButton.hidden = !zoomed;
        this.resetButton.textContent = `${this.resetLabel} · ${Math.round(this.zoom * 100)}%`;
      }

      this._applyImageTransform();
    }

    _applyImageTransform() {
      const image = this._activeImage;

      if (image) {
        image.style.transform = `translate3d(${this.pan.x}px, ${this.pan.y}px, 0) scale(${this.zoom})`;
      }
    }

    _onWheel(event) {
      if (this.hidden) {
        return;
      }

      event.preventDefault();
      this._setZoom(this.zoom - event.deltaY * WHEEL_SENSITIVITY, true);
    }

    _onDoubleClick(event) {
      if (!event.target.closest('.lightbox__slide')) {
        return;
      }

      this._normalizeTrack();
      this._setZoom(this.zoom > MIN_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM);
    }

    /* -- drag: pans while zoomed, pages while not -- */

    _onPointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') {
        return;
      }

      this._normalizeTrack();

      this.drag = {
        x: event.clientX,
        y: event.clientY,
        panX: this.pan.x,
        panY: this.pan.y,
        moved: false,
        id: event.pointerId
      };

      this.viewport.setPointerCapture?.(event.pointerId);
    }

    _onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.id) {
        return;
      }

      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;

      if (!this.drag.moved && Math.hypot(dx, dy) < DRAG_SLOP) {
        return;
      }

      this.drag.moved = true;

      if (this.zoom > MIN_ZOOM) {
        this.pan = { x: this.drag.panX + dx, y: this.drag.panY + dy };
        this._applyImageTransform();
      } else {
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(calc(${-this.trackIndex * 100}% + ${dx}px), 0, 0)`;
      }
    }

    _onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.id) {
        return;
      }

      const dx = event.clientX - this.drag.x;
      const wasDragging = this.drag.moved;
      const zoomed = this.zoom > MIN_ZOOM;

      this.viewport.releasePointerCapture?.(event.pointerId);
      this.drag = null;

      if (zoomed || !wasDragging) {
        return;
      }

      const page = pageFromDrag(dx, this.viewport.clientWidth);
      if (page === 1) {
        this._next();
      } else if (page === -1) {
        this._prev();
      } else {
        this.track.style.transition = '';
        this.track.style.transform = `translate3d(${-this.trackIndex * 100}%, 0, 0)`;
      }
    }

    /* -- controls -- */

    _onClick(event) {
      const target = event.target;

      if (target.closest('[data-lightbox-close]')) {
        this.close();
      } else if (target.closest('[data-lightbox-prev]')) {
        this._prev();
      } else if (target.closest('[data-lightbox-next]')) {
        this._next();
      } else if (target.closest('[data-lightbox-reset]')) {
        this._setZoom(MIN_ZOOM);
      } else if (target.closest('[data-lightbox-zoom-in]')) {
        this._setZoom(this.zoom * 1.4, true);
      } else if (target.closest('[data-lightbox-zoom-out]')) {
        this._setZoom(this.zoom / 1.4, true);
      } else {
        const thumb = target.closest('[data-lightbox-thumb]');

        if (thumb) {
          this._goTo(this.thumbs.indexOf(thumb));
        }
      }
    }

    _onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowLeft' && this.zoom === MIN_ZOOM) {
        this._prev();
      } else if (event.key === 'ArrowRight' && this.zoom === MIN_ZOOM) {
        this._next();
      }
    }
  }

  window.customElements.define('product-lightbox', ProductLightbox);

  // Exposed for assets/meddey-lightbox.test.js.
  window.meddeyLightbox = { wrapIndex, clampZoom, pageFromDrag, MIN_ZOOM, MAX_ZOOM, DOUBLE_TAP_ZOOM };

  /* -- opening it: the magnifier button, or the photo itself -- */

  let pointerStart = null;

  document.addEventListener(
    'pointerdown',
    (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
    },
    true
  );

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-lightbox-open]');
    const slide = event.target.closest?.('.product__media-item[data-media-type="image"]');

    if (!trigger && !slide) {
      return;
    }

    // A swipe on the gallery ends in a click too; only a still tap opens.
    if (
      slide &&
      !trigger &&
      pointerStart &&
      Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > DRAG_SLOP
    ) {
      return;
    }

    const gallery = (trigger || slide).closest('.product__media');
    const lightbox = document.getElementById(gallery?.dataset.lightbox || '');

    if (!lightbox) {
      return;
    }

    event.preventDefault();

    const selected = gallery.querySelector('.product__media-item.is-selected');

    lightbox.open((slide || selected)?.dataset.mediaId);
  });
})();
